import { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Package, Search, CheckCircle, Clock, Truck, XCircle } from 'lucide-react';
import { ordersAPI } from '../lib/api';
import { useApiQuery } from '../hooks/useApiQuery';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

const STATUS_STEPS = [
  { key: 'pending',   label: 'Order Placed',  desc: 'Your order has been received',     icon: Clock },
  { key: 'confirmed', label: 'Confirmed',      desc: 'Order confirmed by our team',       icon: CheckCircle },
  { key: 'delivered', label: 'Delivered',      desc: 'Order delivered to your address',   icon: Truck },
];

const OrderTrackingPage = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail]             = useState('');
  const [order, setOrder]             = useState(null);
  const [searched, setSearched]       = useState(false);

  const { refetch, isFetching: loading } = useApiQuery(
    ['allOrders'],
    () => ordersAPI.getAll(),
    { enabled: false }
  );

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearched(true);
    try {
      const { data: response, isError } = await refetch();
      if (isError || !response) throw new Error('Fetch failed');
      const found = response.data.find(
        (o) => o.order_number === orderNumber && o.customer_email === email
      );
      if (found) { setOrder(found); }
      else { setOrder(null); toast.error('Order not found. Please check your details.'); }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to search order');
    }
  };

  const currentStepIndex = order ? STATUS_STEPS.findIndex(s => s.key === order.status) : -1;
  const isCancelled       = order?.status === 'cancelled';

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Navbar />

      {/* Hero */}
      <section className="hero-gradient py-12 border-b border-slate-200" data-testid="tracking-hero">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h1
            className="text-3xl md:text-4xl font-bold text-slate-800 mb-1"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Track Your Order
          </h1>
          <p className="text-slate-500">Enter your order number and email to view real-time status</p>
        </div>
      </section>

      <section className="py-12 flex-1">
        <div className="container mx-auto px-4 md:px-8 max-w-2xl">

          {/* Search card */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <Search className="w-5 h-5 text-[#8acb95]" />
              </div>
              <div>
                <h2 className="font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Find Your Order
                </h2>
                <p className="text-xs text-slate-400">Details from your order confirmation</p>
              </div>
            </div>

            <form onSubmit={handleSearch} className="space-y-4" data-testid="tracking-form">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Order Number</label>
                <input
                  required
                  placeholder="e.g. GS202603230001"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  data-testid="order-number-input"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] focus:ring-2 focus:ring-[#8acb95]/10 font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address</label>
                <input
                  required
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] focus:ring-2 focus:ring-[#8acb95]/10"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="track-button"
                className="w-full flex items-center justify-center gap-2 bg-[#8acb95] hover:bg-[#7bb885] disabled:bg-slate-300 text-white font-bold py-3.5 rounded-2xl text-sm transition-all shadow-sm"
              >
                <Search className="w-4 h-4" />
                {loading ? 'Searching...' : 'Track Order'}
              </button>
            </form>
          </div>

          {/* Not found */}
          {searched && !order && (
            <div className="text-center py-14 bg-white rounded-2xl border border-slate-100" data-testid="no-order-found">
              <Package className="w-14 h-14 text-slate-200 mx-auto mb-4" />
              <h3 className="font-bold text-slate-700 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Order Not Found
              </h3>
              <p className="text-sm text-slate-400">Please check your order number and email address</p>
            </div>
          )}

          {/* Order details */}
          {order && (
            <div className="space-y-5" data-testid="order-details">

              {/* Header card */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-widest font-medium mb-1">Order</p>
                    <h2 className="text-xl font-bold text-slate-800 font-mono">#{order.order_number}</h2>
                    <p className="text-xs text-slate-400 mt-1">
                      Placed on {new Date(order.created_at).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'long', day: 'numeric',
                      })}
                    </p>
                  </div>
                  {isCancelled ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border bg-red-50 text-red-600 border-red-100">
                      <XCircle className="w-3.5 h-3.5" /> Cancelled
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${
                      order.status === 'delivered'
                        ? 'bg-green-50 text-green-700 border-green-100'
                        : order.status === 'confirmed'
                        ? 'bg-blue-50 text-blue-700 border-blue-100'
                        : 'bg-yellow-50 text-yellow-700 border-yellow-100'
                    }`}>
                      <CheckCircle className="w-3.5 h-3.5" />
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  )}
                </div>

                {/* Visual timeline */}
                {!isCancelled && (
                  <div className="mt-4">
                    <div className="flex items-start">
                      {STATUS_STEPS.map((step, i) => {
                        const isActive = i <= currentStepIndex;
                        const isCurrent = i === currentStepIndex;
                        const Icon = step.icon;
                        return (
                          <div key={step.key} className="flex-1 flex flex-col items-center relative">
                            {/* Connector line */}
                            {i < STATUS_STEPS.length - 1 && (
                              <div className={`absolute left-1/2 top-4 w-full h-0.5 ${
                                isActive && !isCurrent ? 'bg-[#8acb95]' : 'bg-slate-200'
                              }`} />
                            )}
                            {/* Dot */}
                            <div className={`relative z-10 w-8 h-8 rounded-full border-2 flex items-center justify-center mb-2 transition-all ${
                              isActive
                                ? 'bg-[#8acb95] border-[#8acb95]'
                                : 'bg-white border-slate-200'
                            } ${isCurrent ? 'ring-4 ring-[#8acb95]/20' : ''}`}>
                              <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-slate-300'}`} />
                            </div>
                            <p className={`text-[10px] font-semibold text-center ${isActive ? 'text-[#6ab57e]' : 'text-slate-400'}`}>
                              {step.label}
                            </p>
                            <p className="text-[9px] text-slate-400 text-center hidden sm:block">{step.desc}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Delivery info */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <h3 className="font-bold text-slate-700 text-sm mb-3 uppercase tracking-wide">Delivery Info</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-slate-400">Name</p><p className="font-medium">{order.customer_name}</p></div>
                  <div><p className="text-xs text-slate-400">Phone</p><p className="font-medium">{order.customer_phone}</p></div>
                  <div className="sm:col-span-2"><p className="text-xs text-slate-400">Address</p><p className="font-medium">{order.customer_address}</p></div>
                </div>
              </div>

              {/* Items */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <h3 className="font-bold text-slate-700 text-sm mb-3 uppercase tracking-wide">
                  Order Items ({order.items.length})
                </h3>
                <div className="space-y-2.5">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-slate-50 last:border-0">
                      <div className="flex-1 min-w-0 pr-4">
                        <p className="text-sm font-medium text-slate-700 truncate">{item.product_name}</p>
                        <p className="text-xs text-slate-400">{item.product_code}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold">KWD {item.price.toFixed(3)} × {item.quantity}</p>
                        <p className="text-xs text-slate-400">KWD {(item.price * item.quantity).toFixed(3)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-4 mt-2 border-t border-slate-100">
                  <span className="font-bold text-slate-800">Total</span>
                  <span className="text-xl font-bold text-[#8acb95]">KWD {order.total_amount.toFixed(3)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OrderTrackingPage;
